import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import { ticketService } from '../../services/ticketService';
import { useAuth } from '../../contexts/AuthContext';
import TicketDetailModal from './components/TicketDetailModal';
import StatsOverview from './components/StatsOverview';


const SupportTicketSystemAdminPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    search: ''
  });
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchTickets();
    fetchStats();
  }, [userProfile, navigate]);

  useEffect(() => {
    applyFilters();
  }, [tickets, filters]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketService?.getAll();
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err?.message || 'Destek talepleri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await ticketService?.getStatistics();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...tickets];

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(t => t?.status === filters?.status);
    }

    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(t => t?.priority === filters?.priority);
    }

    if (filters?.category !== 'all') {
      filtered = filtered?.filter(t => t?.category === filters?.category);
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter(t => 
        t?.subject?.toLowerCase()?.includes(searchLower) ||
        t?.ticketNumber?.toLowerCase()?.includes(searchLower) ||
        t?.description?.toLowerCase()?.includes(searchLower)
      );
    }

    setFilteredTickets(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      await ticketService?.update(ticketId, { status: newStatus });
      await fetchTickets();
      await fetchStats();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Error updating ticket status:', err);
      alert('Durum güncellenirken bir hata oluştu: ' + err?.message);
    }
  };

  const handlePriorityUpdate = async (ticketId, newPriority) => {
    try {
      await ticketService?.update(ticketId, { priority: newPriority });
      await fetchTickets();
      await fetchStats();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (err) {
      console.error('Error updating ticket priority:', err);
      alert('Öncelik güncellenirken bir hata oluştu: ' + err?.message);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-green-700 bg-green-100',
      medium: 'text-yellow-700 bg-yellow-100',
      high: 'text-orange-700 bg-orange-100',
      urgent: 'text-red-700 bg-red-100'
    };
    return colors?.[priority] || 'text-gray-700 bg-gray-100';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-blue-700 bg-blue-100',
      in_progress: 'text-purple-700 bg-purple-100',
      resolved: 'text-green-700 bg-green-100',
      closed: 'text-gray-700 bg-gray-100'
    };
    return colors?.[status] || 'text-gray-700 bg-gray-100';
  };

  const getStatusText = (status) => {
    const texts = {
      open: 'Açık',
      in_progress: 'İşlemde',
      resolved: 'Çözüldü',
      closed: 'Kapalı'
    };
    return texts?.[status] || status;
  };

  const getPriorityText = (priority) => {
    const texts = {
      low: 'Düşük',
      medium: 'Orta',
      high: 'Yüksek',
      urgent: 'Acil'
    };
    return texts?.[priority] || priority;
  };

  const getCategoryText = (category) => {
    const texts = {
      technical_issues: 'Teknik Sorunlar',
      billing: 'Faturalama',
      account: 'Hesap',
      feature_request: 'Özellik İsteği',
      bug_report: 'Hata Bildirimi',
      general_inquiry: 'Genel Sorular'
    };
    return texts?.[category] || category;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 mt-20">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
            <Icon name="Headphones" size={32} />
            Destek Talepleri Yönetim Paneli
          </h1>
          <p className="text-muted-foreground">Kullanıcı destek taleplerini yönetin ve yanıtlayın</p>
        </div>

        {stats && <StatsOverview stats={stats} />}

        <div className="bg-card rounded-xl shadow-organic p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Talep numarası veya konu ile ara..."
                  value={filters?.search}
                  onChange={(e) => handleFilterChange('search', e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={filters?.status}
                onChange={(e) => handleFilterChange('status', e?.target?.value)}
                className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="open">Açık</option>
                <option value="in_progress">İşlemde</option>
                <option value="resolved">Çözüldü</option>
                <option value="closed">Kapalı</option>
              </select>
              <select
                value={filters?.priority}
                onChange={(e) => handleFilterChange('priority', e?.target?.value)}
                className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Tüm Öncelikler</option>
                <option value="low">Düşük</option>
                <option value="medium">Orta</option>
                <option value="high">Yüksek</option>
                <option value="urgent">Acil</option>
              </select>
              <select
                value={filters?.category}
                onChange={(e) => handleFilterChange('category', e?.target?.value)}
                className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">Tüm Kategoriler</option>
                <option value="technical_issues">Teknik Sorunlar</option>
                <option value="billing">Faturalama</option>
                <option value="account">Hesap</option>
                <option value="feature_request">Özellik İsteği</option>
                <option value="bug_report">Hata Bildirimi</option>
                <option value="general_inquiry">Genel Sorular</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Destek talepleri yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} className="text-red-600" />
              </div>
              <p className="text-red-600 mb-4">{error}</p>
              <Button variant="outline" onClick={fetchTickets}>
                Tekrar Dene
              </Button>
            </div>
          ) : filteredTickets?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Inbox" size={32} className="text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">Destek talebi bulunamadı</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Talep No</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Konu</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Kategori</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Öncelik</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Durum</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Tarih</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets?.map((ticket) => (
                    <tr key={ticket?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm text-primary">{ticket?.ticketNumber}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="max-w-xs">
                          <p className="font-medium text-foreground line-clamp-1">{ticket?.subject}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">{ticket?.description}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">{getCategoryText(ticket?.category)}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(ticket?.priority)}`}>
                          {getPriorityText(ticket?.priority)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket?.status)}`}>
                          {getStatusText(ticket?.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">
                          {new Date(ticket?.createdAt)?.toLocaleDateString('tr-TR')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Eye"
                          onClick={() => handleTicketClick(ticket)}
                        >
                          Görüntüle
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showDetailModal && selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setShowDetailModal(false)}
          onStatusUpdate={handleStatusUpdate}
          onPriorityUpdate={handlePriorityUpdate}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          getStatusText={getStatusText}
          getPriorityText={getPriorityText}
          getCategoryText={getCategoryText}
        />
      )}
    </div>
  );
};

export default SupportTicketSystemAdminPanel;